import type { Schema } from '@dealdesk/api-schema';
import { z } from 'zod';

import { M, zMoney, zOptionalMoney } from '@/business';
import { isValidUaeIban } from '@/shared/lib/identifiers';

import { initialRateType, RATE_TYPES } from '../model/rate';
import { applicationShape, applicationUpdate, hrShape, hrUpdate, toApplicationValues } from './application';

type Field = Schema<'ConfigField'>;

/** Bank-specific fields from the configuration version → Zod (restricted, known types only). */
export function schemaForBankFields(fields: readonly Field[]) {
  const shape: Record<string, z.ZodType> = {};
  for (const f of fields) {
    switch (f.type) {
      case 'boolean':
        shape[f.key] = z.boolean().nullable().optional();
        break;
      case 'integer':
        shape[f.key] = z.union([z.literal(''), z.string().regex(/^\d+$/, M.invalidAmount)]).nullable().optional();
        break;
      case 'money':
        shape[f.key] = zOptionalMoney();
        break;
      default:
        shape[f.key] = z.string().nullable().optional();
    }
  }
  return z.object(shape);
}

export const INCOME_KINDS = ['basic', 'hra_allowance', 'air_ticket_allowance', 'education_allowance', 'bonus', 'rental_income', 'overtime_commission', 'allowance', 'add_back', 'eosb', 'pension', 'other'] as const satisfies readonly Schema<'IncomeComponentKind'>[];
export const FACILITY_TYPES = ['personal_finance', 'credit_card', 'auto_finance', 'home_finance', 'overdraft', 'other'] as const satisfies readonly Schema<'FacilityType'>[];
export const ACCOUNT_STATUSES = ['active', 'dormant', 'blacklisted', 'none'] as const satisfies readonly Schema<'AccountStatus'>[];
/** Values the server returns masked (IBAN, account number): shown as stored, never validated or sent back. */
const MASKED = /•/;

/** B2 · profit rate offered to the customer: percent 0–100 with up to four decimals (as the RATE of forms/calculator). */
const PERCENT = /^\d{1,3}(\.\d{1,4})?$/;
const PERCENT_MAX = /^(\d{1,2}(\.\d+)?|100(\.0+)?)$/;
export const isProfitRate = (v: string) => PERCENT.test(v.trim()) && PERCENT_MAX.test(v.trim());
export const zProfitRate = () =>
  z
    .string()
    .nullable()
    .optional()
    .refine((v) => v === null || v === undefined || v.trim() === '' || isProfitRate(v), 'validation.profitRate');

const employmentRow = z.object({
  id: z.string().optional(),
  employerId: z.string({ error: M.required }).min(1, M.required),
  /** Display only (not sent). */
  employerName: z.string().optional(),
  joinedOn: z.string().nullable().optional(),
  salary: zMoney(),
  isPrimary: z.boolean(),
  ...hrShape,
});

const incomeRow = z.object({
  id: z.string().optional(),
  employmentId: z.string().optional(),
  kind: z.enum(INCOME_KINDS),
  label: z.string().optional(),
  monthlyAmount: zMoney(),
});

const settlementRow = z.object({
  bankId: z.string({ error: M.required }).min(1, M.required),
  facilityType: z.enum(FACILITY_TYPES),
  liabilityAmount: zOptionalMoney(),
  isMain: z.boolean(),
});

/**
 * Drafts may be incomplete: required-ness is checked by the server when the deal is submitted to operations. Client
 * validation covers formats and ranges, and each row the user adds must be complete (employer + salary, bank…).
 */
export function dealFormSchema(fields: readonly Field[]) {
  return z.object({
    schemeId: z.string().nullable().optional(),
    employments: z.array(employmentRow),
    income: z.array(incomeRow),
    relationship: z.object({
      relationship: z.enum(['new', 'existing', 'unknown']),
      cif: z.string().regex(/^\d{6,12}$/, 'validation.cif').or(z.literal('')).optional(),
      // A stored account number comes back masked (•••• 0000): it stays valid and is never sent back.
      accountNo: z.string().regex(/^[\d\s]{8,20}$/, 'validation.accountNo').or(z.literal('')).or(z.string().regex(MASKED)).optional(),
      accountStatus: z.enum(ACCOUNT_STATUSES),
    }),
    settles: z.boolean(),
    settlementBanks: z.array(settlementRow),
    requestedAmount: zOptionalMoney({ min: '1.00' }),
    tenorMonths: z.number().int().min(6, M.tenor).max(300, M.tenor).nullable().optional(),
    firstInstalmentOn: z.string().nullable().optional(),
    profitRate: zProfitRate(),
    /** C1: rate type code; the select only offers the version's allowed types (the server checks them again). */
    rateType: z.enum(RATE_TYPES).nullable().optional(),
    iban: z
      .string()
      .nullable()
      .optional()
      .refine((v) => !v || MASKED.test(v) || isValidUaeIban(v), M.iban),
    cardLimit: zOptionalMoney(),
    cardType: z.string().nullable().optional(),
    propertyValue: zOptionalMoney(),
    propertyEmirate: z.string().nullable().optional(),
    downPayment: zOptionalMoney(),
    companyName: z.string().nullable().optional(),
    tradeLicenseNo: z.string().nullable().optional(),
    annualTurnover: zOptionalMoney(),
    extraFields: schemaForBankFields(fields),
    ...applicationShape,
  });
}

export type DealFormValues = z.infer<ReturnType<typeof dealFormSchema>>;

export function toFormValues(form: Pick<Schema<'DealForm'>, 'values' | 'bankFields' | 'employments'> & Partial<Pick<Schema<'DealForm'>, 'rate'>>): DealFormValues {
  const v = form.values;
  const extra: Record<string, string | boolean | null> = {};
  for (const f of form.bankFields) extra[f.key] = v.extraFields?.[f.key] ?? (f.type === 'boolean' ? false : null);
  const names = new Map((form.employments ?? []).map((e) => [e.employerId, e.employerName]));
  const rel = v.bankRelationship;
  return {
    schemeId: v.schemeId ?? null,
    employments: (v.employments ?? []).map((e) => ({ ...(e.id ? { id: e.id } : {}), employerId: e.employerId, employerName: names.get(e.employerId) ?? '', joinedOn: e.joinedOn ?? null, salary: e.salary, isPrimary: Boolean(e.isPrimary), hrContactName: e.hrContactName ?? null, hrPhone: e.hrPhone ?? null, hrEmail: e.hrEmail ?? null })),
    income: (v.income ?? []).map((i) => ({ ...(i.id ? { id: i.id } : {}), ...(i.employmentId ? { employmentId: i.employmentId } : {}), kind: i.kind, label: i.label ?? '', monthlyAmount: i.monthlyAmount })),
    relationship: rel ? { relationship: rel.relationship, cif: rel.cif ?? '', accountNo: rel.accountNo ?? '', accountStatus: rel.accountStatus } : { relationship: 'unknown', cif: '', accountNo: '', accountStatus: 'none' },
    settles: Boolean(v.settlementBanks?.length),
    settlementBanks: (v.settlementBanks ?? []).map((x) => ({ bankId: x.bankId, facilityType: x.facilityType, liabilityAmount: x.liabilityAmount ?? null, isMain: Boolean(x.isMain) })),
    requestedAmount: v.requestedAmount ?? null,
    tenorMonths: v.tenorMonths ?? null,
    firstInstalmentOn: v.firstInstalmentOn ?? null,
    profitRate: v.profitRate ?? null,
    // C1: the deal's rate type, else the version's default (the server records the default with the rate).
    rateType: form.rate ? initialRateType(form.rate, v.rateType) : (v.rateType ?? null),
    iban: v.iban ?? null,
    cardLimit: v.cardLimit ?? null,
    cardType: v.cardType ?? null,
    propertyValue: v.propertyValue ?? null,
    propertyEmirate: v.propertyEmirate ?? null,
    downPayment: v.downPayment ?? null,
    companyName: v.companyName ?? null,
    tradeLicenseNo: v.tradeLicenseNo ?? null,
    annualTurnover: v.annualTurnover ?? null,
    extraFields: extra,
    ...toApplicationValues(v),
  };
}

const V2_KEYS = ['employments', 'income', 'relationship', 'settles', 'settlementBanks'] as const;
/** A2 keys: mapped by applicationUpdate (masked values left out, emptied values cleared). */
const A2_KEYS = ['email', 'officialEmail', 'salaryTransfer', 'retiringBankCode', 'retiringBankIban', 'profile', 'addresses', 'liabilities', 'references'] as const;

/** Only changed fields are sent, so concurrent edits to other fields don't collide. Sections go as a whole. */
export function toUpdate(values: DealFormValues, dirty: Partial<Record<keyof DealFormValues, unknown>>, product: Schema<'ProductCode'>): Schema<'DealUpdate'> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(dirty) as (keyof DealFormValues)[]) {
    if ((V2_KEYS as readonly string[]).includes(key) || (A2_KEYS as readonly string[]).includes(key)) continue;
    if (key === 'extraFields') out['extraFields'] = values.extraFields;
    else if (key === 'profitRate') out['profitRate'] = values.profitRate?.trim() || null;
    else out[key] = values[key] === '' ? null : values[key];
  }
  if ('employments' in dirty) out['employments'] = values.employments.map(({ employerName: _name, hrContactName: _n, hrPhone: _p, hrEmail: _e, ...e }) => ({ ...e, joinedOn: e.joinedOn || null, ...hrUpdate({ hrContactName: _n, hrPhone: _p, hrEmail: _e }) }));
  if ('income' in dirty) out['income'] = values.income.map((i) => ({ ...i, label: i.label?.trim() || undefined }));
  if ('relationship' in dirty) {
    const r = values.relationship;
    out['bankRelationship'] = r.relationship === 'unknown' ? null : { relationship: r.relationship, cif: r.cif || null, ...(r.accountNo && MASKED.test(r.accountNo) ? {} : { accountNo: r.accountNo?.replace(/\s/g, '') || null }), accountStatus: r.accountStatus };
  }
  Object.assign(out, applicationUpdate(values, dirty as Record<string, Record<string, unknown> | boolean | undefined>));
  // One liabilities list drives the settlement plan: never send settlementBanks with it.
  if (!('liabilities' in out) && ('settles' in dirty || 'settlementBanks' in dirty)) out['settlementBanks'] = values.settles ? values.settlementBanks.map((x) => ({ bankId: x.bankId, facilityType: x.facilityType, isMain: x.isMain, ...(x.liabilityAmount ? { liabilityAmount: x.liabilityAmount } : {}) })) : [];
  if (product === 'CC' && 'cardLimit' in out) out['requestedAmount'] = values.cardLimit;
  return out;
}
